import type { FunctionComponent } from "react";
import { useEffect, useRef, useState } from "react";
import AdsTraffic from "app/pages/website-analysis/analyze-publishers/common/ads-traffic/AdsTraffic";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { i18nFilter } from "app/filters/i18nFilter";
import { DefaultFetchService } from "app/services/fetchService";
import DurationService from "app/services/DurationService";
import CountryService from "app/services/CountryService";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import { useCurrentStateAndParams } from "@uirouter/react";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import Sentry from "@similarweb/sentry-logger";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { PngExportService } from "app/services/PngExportService";
import { formatDatesRangeString } from "app/pages/website-analysis/analyze-publishers/common/ads-traffic/AdsTrafficChartParsers";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { allTrackers } from "app/services/track/track";

const TitleContainer = styled.div`
    padding: 20px;
    border-bottom: 1px solid ${colorsPalettes.carbon[50]};
`;

const ButtonsContainer = styled(FlexRow)`
    > div {
        margin-left: 8px;
    }
`;

export const AdsTrafficContainer: FunctionComponent<any> = ({ chosenItems, isMonetizationPage = false }) => {
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const i18n = i18nFilter();
    const fetchService = DefaultFetchService.getInstance();
    const { params } = useCurrentStateAndParams();
    const { country, key, duration, isWWW, webSource } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const title = i18n("analysis.publishers.ads.traffic.title");
    const tooltip = i18n("analysis.publishers.ads.traffic.tooltip");
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from,
                to,
                format: "MMMM YYYY",
            },
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country)?.text,
        },
        {
            filter: "webSource",
            value: webSource,
        },
    ];
    const isCompare = key.split(",").length > 1;
    const mapSum = (arr) => arr.map((item) => Math.floor(item[0] + item[1]));
    const chartRef = useRef(null);

    const transformData = (response) => {
        const data = response.dictionary;
        if (!isCompare && data[key] && Object.keys(data[key]).length > 0) {
            return {
                data: mapSum(data[key].Volumes),
                dates: data[key].Dates,
            };
        } else if (isCompare) {
            const sites = key.split(",");
            const nullCounter = sites.reduce((results, site) => {
                if (!data[site] || Object.keys(data[site]).length === 0) {
                    results++;
                }
                return results;
            }, 0);
            if (sites.length === nullCounter) {
                return null;
            }
            return {
                data: sites.map((site) => {
                    return {
                        displayName: site,
                        data: data[site].Volumes ? mapSum(data[site]?.Volumes) : null,
                        dates: data[site]?.Dates || null,
                        total: data[site]?.VolumeTotal || null,
                    };
                }),
            };
        }
        return null;
    };

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            try {
                const response = await fetchService.get<any>("/api/websiteanalysis/GetTrafficDisplayPaidOutgoing", {
                    country,
                    from,
                    isWWW: isWWW !== "*",
                    isWindow,
                    key,
                    to,
                });
                const transformedData = transformData(response);
                setData(transformedData);
            } catch (e) {
                Sentry.error("error fetching Traffic Display Paid Outgoing data", e);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, []);

    const getComponent = () =>
        !data || (!isCompare && data.length === 0) ? (
            <NoDataLandscape
                title="analysis.publishers.ads.traffic.empty.state.title"
                subtitle={null}
            />
        ) : (
            <AdsTraffic
                data={data}
                params={params}
                chosenItems={chosenItems}
                afterRender={(chart) => {
                    chartRef.current = chart;
                    return {};
                }}
            />
        );

    const handleOnExport = (): Promise<void> => {
        return new Promise((resolve) => {
            if (!chartRef.current) {
                resolve();
                return;
            }
            allTrackers.dispatchTrackEvent("Download", "submit-ok", "Analyze Publishers/Traffic Ads/Download/png");
            const exportTitle = `Outgoing Ads Chart ${formatDatesRangeString(
                isCompare ? data.data[0].dates : data.dates,
            )}`;
            PngExportService.getInstance().export(chartRef.current, exportTitle);
            resolve();
        });
    };
    return (
        <>
            <TitleContainer>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subTitleFilters}
                >
                    <ButtonsContainer>
                        <DownloadButtonMenu
                            handleDownloadFunction={handleOnExport}
                            PNG={true}
                        />
                        <AddToDashboard
                            metric={
                                isMonetizationPage
                                    ? "DashboardTrafficSourcesAds"
                                    : "DashboardTrafficSourcesAdsAdvertisers"
                            }
                            type="Graph"
                            modelType="fromWebsite"
                            webSource={devicesTypes.DESKTOP as IWidgetModelTypesWebSource}
                            filters={{ ...params.filtersStateObject }}
                        />
                    </ButtonsContainer>
                </WidgetTitle>
            </TitleContainer>
            {isLoading ? <WidgetLoader /> : getComponent()}
        </>
    );
};
